import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { supplierUi } from "../../../shared/supplierUi";

const emptyValue = (val) => val === null || val === undefined || val === "";

const DetailValue = ({ label, display }) => {
	const textRef = useRef(null);
	const [truncated, setTruncated] = useState(false);
	const isEmpty = display === "—";

	useEffect(() => {
		const el = textRef.current;
		if (!el || isEmpty) {
			setTruncated(false);
			return;
		}
		const check = () => setTruncated(el.scrollWidth > el.clientWidth + 1);
		check();
		window.addEventListener("resize", check);
		return () => window.removeEventListener("resize", check);
	}, [display, isEmpty]);

	const valueTypography = (
		<Typography
			ref={textRef}
			noWrap
			sx={{
				overflow: "hidden",
				width: "100%",
				textOverflow: "ellipsis",
				fontSize: 17,
				fontWeight: 500,
				color: isEmpty ? supplierUi.textMuted : supplierUi.textPrimary,
				lineHeight: 1.4,
				cursor: truncated ? "help" : "default",
			}}>
			{display}
		</Typography>
	);

	if (isEmpty || !truncated) return valueTypography;

	return (
		<Tooltip
			arrow
			placement="top-start"
			enterDelay={250}
			leaveDelay={100}
			describeChild
			title={
				<Box sx={{ py: 0.25, maxWidth: 360 }}>
					<Typography
						sx={{
							fontSize: 11,
							fontWeight: 600,
							letterSpacing: 0.4,
							textTransform: "uppercase",
							color: grey[400],
							mb: 0.5,
						}}>
						{label}
					</Typography>
					<Typography
						sx={{
							fontSize: 14,
							fontWeight: 500,
							lineHeight: 1.45,
							color: "#fff",
							whiteSpace: "pre-wrap",
							wordBreak: "break-word",
						}}>
						{display}
					</Typography>
				</Box>
			}
			componentsProps={{
				tooltip: {
					sx: {
						bgcolor: grey[900],
						borderRadius: 1.5,
						px: 1.5,
						py: 1,
						maxWidth: 380,
						boxShadow: "0 8px 24px rgba(16, 24, 40, 0.18)",
					},
				},
				arrow: {
					sx: { color: grey[900] },
				},
			}}
			PopperProps={{
				modifiers: [{ name: "offset", options: { offset: [0, -4] } }],
			}}>
			<span style={{ display: "block", width: "100%", minWidth: 0 }}>{valueTypography}</span>
		</Tooltip>
	);
};

const DisplayInfo = ({ labelData, values }) => {
	return (
		<Box
			width="100%"
			display="grid"
			gridTemplateColumns={{
				xs: "1fr",
				sm: "repeat(2, minmax(0, 1fr))",
				md: "repeat(3, minmax(0, 1fr))",
			}}
			columnGap={3}
			rowGap={2.5}>
			{Object.keys(labelData).map((colName) => {
				const isDate = labelData[colName]?.isDate;
				const raw = values?.[colName];
				const display = emptyValue(raw)
					? "—"
					: isDate
					? moment(raw).format("DD/MM/YYYY")
					: String(raw);
				const label = labelData[colName]?.name || colName;

				return (
					<Stack spacing={0.5} key={colName} minWidth={0}>
						<Typography
							variant="caption"
							sx={{
								color: supplierUi.textMuted,
								fontWeight: 500,
								letterSpacing: 0.3,
								textTransform: "uppercase",
								fontSize: 13,
							}}>
							{label}
						</Typography>
						<DetailValue label={label} display={display} />
					</Stack>
				);
			})}
		</Box>
	);
};

export default DisplayInfo;
